import { Round, RoundResult, Team } from '~/apis/ssf';
import { useRoundResultsListQuery } from '../queries';
import { divisionIdParamSchema } from '~/schemas';
import { Table } from '~/components/Table/Table';
import { Link } from '~/components/Link/Link';
import { useParams } from '~/hooks/useParams';
import { formatPoints } from '~/utils/points';

interface Props {
  rounds: Round[];
  teams: Team[];
}

function Row({
  team,
  placement,
  results,
  rounds,
}: {
  team: Team;
  placement: number;
  results: (RoundResult | undefined)[];
  rounds: Round[];
}) {
  return (
    <Table.Row>
      <Table.Col>{placement}</Table.Col>
      <Table.Col>
        <Link to={`teams/${team.id}`}>{team.name}</Link>
      </Table.Col>
      {rounds.map((round, index) => (
        <Table.Col key={round.id}>{results[index] ? formatPoints(results[index].points) : '-'}</Table.Col>
      ))}
      <Table.Col>{formatPoints(team.points)}</Table.Col>
    </Table.Row>
  );
}

export function RoundResultsListTable({ rounds, teams }: Props) {
  const { divisionId } = useParams(divisionIdParamSchema.parse);
  const { data, isLoading } = useRoundResultsListQuery({ division: divisionId });

  const mapByTeam =
    data?.results.reduce(
      (acc: Record<number, (RoundResult | undefined)[]>, result) => {
        const teamId = result.team.id;
        if (!acc[teamId]) {
          acc[teamId] = new Array(rounds.length).fill(undefined) as (RoundResult | undefined)[];
        }
        const roundIndex = rounds.findIndex(round => round.id === result.round.id);
        if (roundIndex !== -1) {
          acc[teamId][roundIndex] = result;
        }
        return acc;
      },
      {} as Record<number, (RoundResult | undefined)[]>
    ) ?? [];

  const sortedTeams = teams.sort((a, b) => b.points - a.points);

  return (
    <Table bg="even">
      <Table.Head>
        <Table.Row>
          <Table.Col as="th">#</Table.Col>
          <Table.Col as="th">Lag</Table.Col>
          {rounds.map(round => (
            <Table.Col as="th" key={round.id}>
              {round.name}
            </Table.Col>
          ))}
          <Table.Col as="th">Total</Table.Col>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {isLoading ? (
          <Table.Loading cols={7} />
        ) : (
          sortedTeams.map((team, index) => (
            <Row team={team} placement={index + 1} results={mapByTeam[team.id] ?? []} rounds={rounds} key={team.id} />
          ))
        )}
      </Table.Body>
    </Table>
  );
}
